import { useState, useEffect, useCallback } from 'react';
import { EventTimeline } from '../constants/matrixEvents';
import { cloneDeep } from 'lodash';
import { useMatrixClient } from '../context/MatrixContext';
import {
  JOIN_RULES,
  HISTORY_VISIBILITY,
  PERMISSION_EVENTS,
  POWER_LEVELS,
} from '../constants/roomSettings';
import { GEMATIK_ROOM } from '../constants/room';
import {
  getRoomDisplayName,
  getRoomTopic,
  canUserEditRoomName,
  canUserEditRoomTopic,
  dualWriteRoomName,
  dualWriteRoomTopic,
} from '../utils/gematikRoomUtils';
import matrixService from '../services/matrixService';
import logger from '../utils/logger';

/**
 * Deep clone power levels object to prevent state mutations.
 * Preserves all fields including custom notification levels and unknown keys.
 * Uses lodash.cloneDeep for reliable deep cloning across all browsers.
 *
 * @param {Object} powerLevels - The power levels object to clone
 * @returns {Object} - Deep cloned power levels object with all fields preserved
 */
const deepClonePowerLevels = (powerLevels) => {
  if (!powerLevels || typeof powerLevels !== 'object') {
    return {};
  }
  return cloneDeep(powerLevels);
};

/**
 * Validate power levels structure before sending to server.
 * Validates known fields while allowing unknown fields to pass through.
 * This preserves custom notification levels and future Matrix spec additions.
 *
 * @param {Object} powerLevels - Power levels to validate
 * @throws {Error} - If validation fails for known fields
 */
const validatePowerLevels = (powerLevels) => {
  if (!powerLevels || typeof powerLevels !== 'object') {
    throw new Error('Power levels must be an object');
  }

  // Validate known numeric fields (allows unknown fields to pass through)
  const numericFields = [
    'users_default',
    'events_default',
    'state_default',
    'invite',
    'kick',
    'ban',
    'redact',
  ];

  for (const field of numericFields) {
    if (powerLevels[field] !== undefined) {
      const value = powerLevels[field];
      if (typeof value !== 'number' || !Number.isInteger(value)) {
        throw new Error(`Power level field '${field}' must be an integer, got ${typeof value}`);
      }
      if (value < 0) {
        throw new Error(`Power level field '${field}' must be >= 0, got ${value}`);
      }
    }
  }

  // Validate users map if present
  if (powerLevels.users && typeof powerLevels.users === 'object') {
    for (const [userId, level] of Object.entries(powerLevels.users)) {
      if (typeof level !== 'number' || !Number.isInteger(level)) {
        throw new Error(`Power level for user ${userId} must be an integer`);
      }
      if (level < 0) {
        throw new Error(`Power level for user ${userId} must be >= 0`);
      }
    }
  }

  // Validate events map if present
  if (powerLevels.events && typeof powerLevels.events === 'object') {
    for (const [eventType, level] of Object.entries(powerLevels.events)) {
      if (typeof level !== 'number' || !Number.isInteger(level)) {
        throw new Error(`Power level for event ${eventType} must be an integer`);
      }
      if (level < 0) {
        throw new Error(`Power level for event ${eventType} must be >= 0`);
      }
    }
  }
};

/**
 * Hook for managing room settings (security, permissions, notifications)
 * Separate from useRoomEdit which is for participant/avatar management
 *
 * @param {string} roomId - The room ID to manage settings for
 * @returns {Object} Room settings state and actions
 */
export const useRoomSettings = (roomId) => {
  const client = useMatrixClient();
  const [room, setRoom] = useState(null);
  const [roomName, setRoomName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isDM, setIsDM] = useState(false);
  const [isEncrypted, setIsEncrypted] = useState(false);

  // Security & Privacy settings
  const [joinRule, setJoinRule] = useState(JOIN_RULES.INVITE);
  const [historyVisibility, setHistoryVisibility] = useState(HISTORY_VISIBILITY.INVITED);

  // Permissions settings (power levels)
  const [powerLevels, setPowerLevels] = useState(null);
  const [permissionLevels, setPermissionLevels] = useState({
    [PERMISSION_EVENTS.DEFAULT]: POWER_LEVELS.DEFAULT,
    [PERMISSION_EVENTS.SEND_MESSAGE]: POWER_LEVELS.DEFAULT,
    [PERMISSION_EVENTS.CONFIGURE]: POWER_LEVELS.MODERATOR,
    [PERMISSION_EVENTS.BAN]: POWER_LEVELS.MODERATOR,
    [PERMISSION_EVENTS.KICK]: POWER_LEVELS.MODERATOR,
    [PERMISSION_EVENTS.REDACT]: POWER_LEVELS.MODERATOR,
    [PERMISSION_EVENTS.INVITE]: POWER_LEVELS.MODERATOR,
    [PERMISSION_EVENTS.HISTORICAL]: POWER_LEVELS.MODERATOR,
  });

  // Room details
  const [roomTopic, setRoomTopic] = useState('');
  const [roomVersion, setRoomVersion] = useState('');
  const [canEditName, setCanEditName] = useState(false);
  const [canEditTopic, setCanEditTopic] = useState(false);

  // Track if user can configure room settings
  const [canConfigure, setCanConfigure] = useState(false);

  // Track current user's power level
  const [userPowerLevel, setUserPowerLevel] = useState(0);

  // Load room object
  useEffect(() => {
    if (!client || !roomId) {
      setRoom(null);
      setRoomName('');
      setRoomTopic('');
      setRoomVersion('');
      setIsDM(false);
      setIsEncrypted(false);
      setIsLoading(false);
      return;
    }

    const roomObject = client.getRoom(roomId);
    if (roomObject) {
      setRoom(roomObject);
      setRoomName(getRoomDisplayName(roomObject));
      setRoomTopic(getRoomTopic(roomObject));
      setRoomVersion(roomObject.getVersion?.() || '');

      // Check if room is a DM
      setIsDM(matrixService.isDMRoom(roomId));

      // Check if room is encrypted
      const encryptionEvent = roomObject.currentState.getStateEvents('m.room.encryption', '');
      setIsEncrypted(!!encryptionEvent);
    }
    setIsLoading(false);
  }, [client, roomId]);

  // Check if current user can configure room settings
  useEffect(() => {
    if (!room || !client) {
      setCanConfigure(false);
      return;
    }

    const checkCanConfigure = () => {
      try {
        const userId = client.getUserId();
        const powerLevelsEvent = room
          .getLiveTimeline()
          .getState(EventTimeline.FORWARDS)
          .getStateEvents('m.room.power_levels', '');
        
        if (!powerLevelsEvent) {
          setCanConfigure(false);
          return;
        }

        const powerLevelsContent = powerLevelsEvent.getContent();
        // Check against state_default to determine if user can configure room settings
        // (join rules, history visibility, etc.). This is controlled by the "Configure chat" permission.
        const stateDefaultLevel = powerLevelsContent.state_default ?? 50;
        const currentUserPowerLevel = powerLevelsContent.users?.[userId] ?? powerLevelsContent.users_default ?? 0;

        // Update user power level state
        setUserPowerLevel(currentUserPowerLevel);

        // User can configure room settings if they meet the state_default requirement
        setCanConfigure(currentUserPowerLevel >= stateDefaultLevel);

        // Per-field edit permissions for Details page
        setCanEditName(canUserEditRoomName(powerLevelsContent, currentUserPowerLevel));
        setCanEditTopic(canUserEditRoomTopic(powerLevelsContent, currentUserPowerLevel));
      } catch (error) {
        logger.error('Error checking room configuration permission:', error);
        setCanConfigure(false);
        setCanEditName(false);
        setCanEditTopic(false);
      }
    };

    // Check initially
    checkCanConfigure();

    // Listen for power level changes
    const handlePowerLevelChange = (event) => {
      if (event.getType() === 'm.room.power_levels') {
        logger.info('Power levels changed, rechecking canConfigure');
        checkCanConfigure();
      }
    };

    room.on('RoomState.events', handlePowerLevelChange);

    return () => {
      room.off('RoomState.events', handlePowerLevelChange);
    };
  }, [room, client]);

  // Load current room settings and listen for changes
  useEffect(() => {
    if (!room) return;

    const loadSettings = () => {
      try {
        // Load join rule
        const joinRuleEvent = room.currentState.getStateEvents('m.room.join_rules', '');
        if (joinRuleEvent) {
          const rule = joinRuleEvent.getContent()?.join_rule || JOIN_RULES.INVITE;
          setJoinRule(rule);
        }

        // Load history visibility
        const historyEvent = room.currentState.getStateEvents('m.room.history_visibility', '');
        if (historyEvent) {
          const visibility = historyEvent.getContent()?.history_visibility || HISTORY_VISIBILITY.INVITED;
          setHistoryVisibility(visibility);
        }

        // Load power levels
        const powerLevelsEvent = room.currentState.getStateEvents('m.room.power_levels', '');
        if (powerLevelsEvent) {
          const content = powerLevelsEvent.getContent();
          setPowerLevels(content);

          // Extract permission levels
          const levels = {
            [PERMISSION_EVENTS.DEFAULT]: content.users_default ?? POWER_LEVELS.DEFAULT,
            // Per Matrix spec: m.room.message uses events_default, not events['m.room.message']
            // See: https://spec.matrix.org/v1.11/client-server-api/#mroompower_levels
            [PERMISSION_EVENTS.SEND_MESSAGE]: content.events_default ?? POWER_LEVELS.DEFAULT,
            [PERMISSION_EVENTS.CONFIGURE]: content.state_default ?? POWER_LEVELS.MODERATOR,
            [PERMISSION_EVENTS.BAN]: content.ban ?? POWER_LEVELS.MODERATOR,
            [PERMISSION_EVENTS.KICK]: content.kick ?? POWER_LEVELS.MODERATOR,
            [PERMISSION_EVENTS.REDACT]: content.redact ?? POWER_LEVELS.MODERATOR,
            [PERMISSION_EVENTS.INVITE]: content.invite ?? POWER_LEVELS.MODERATOR,
            [PERMISSION_EVENTS.HISTORICAL]: content.events?.['m.room.history_visibility'] ?? content.state_default ?? POWER_LEVELS.MODERATOR,
          };

          setPermissionLevels(levels);
        }
      } catch (error) {
        logger.error('Error loading room settings:', error);
      }
    };

    // Load settings initially
    loadSettings();

    // Listen for state changes
    const handleStateEvents = (event) => {
      const eventType = event.getType();

      if (eventType === 'm.room.join_rules' ||
          eventType === 'm.room.history_visibility' ||
          eventType === 'm.room.power_levels') {
        logger.info('Room settings changed, reloading:', eventType);
        loadSettings();
      }

      // Refresh name/topic when state events change
      if (eventType === 'm.room.name' || eventType === GEMATIK_ROOM.ROOM_NAME) {
        setRoomName(getRoomDisplayName(room));
      }
      if (eventType === 'm.room.topic' || eventType === GEMATIK_ROOM.ROOM_TOPIC) {
        setRoomTopic(getRoomTopic(room));
      }
    };

    room.on('RoomState.events', handleStateEvents);

    return () => {
      room.off('RoomState.events', handleStateEvents);
    };
  }, [room]);

  // Update join rule
  const updateJoinRule = useCallback(async (newRule) => {
    if (!client || !roomId || !canConfigure) {
      throw new Error('Cannot update join rule: insufficient permissions or missing data');
    }

    try {
      await client.sendStateEvent(roomId, 'm.room.join_rules', {
        join_rule: newRule,
      }, '');
      setJoinRule(newRule);
      logger.info('Updated join rule:', newRule);
    } catch (error) {
      logger.error('Error updating join rule:', error);
      throw error;
    }
  }, [client, roomId, canConfigure]);

  // Update history visibility
  const updateHistoryVisibility = useCallback(async (newVisibility) => {
    if (!client || !roomId || !canConfigure) {
      throw new Error('Cannot update history visibility: insufficient permissions or missing data');
    }

    try {
      await client.sendStateEvent(roomId, 'm.room.history_visibility', {
        history_visibility: newVisibility,
      }, '');
      setHistoryVisibility(newVisibility);
      logger.info('Updated history visibility:', newVisibility);
    } catch (error) {
      logger.error('Error updating history visibility:', error);
      throw error;
    }
  }, [client, roomId, canConfigure]);

  // Update permission level for a specific function
  const updatePermissionLevel = useCallback(async (permissionKey, newLevel) => {
    if (!client || !roomId || !canConfigure || !powerLevels) {
      throw new Error('Cannot update permission: insufficient permissions or missing data');
    }

    try {
      // Deep clone current power levels to prevent mutations
      const newPowerLevels = deepClonePowerLevels(powerLevels);

      // Update the specific permission
      switch (permissionKey) {
      case PERMISSION_EVENTS.DEFAULT:
        newPowerLevels.users_default = newLevel;
        break;
      case PERMISSION_EVENTS.SEND_MESSAGE:
        // Per Matrix spec: m.room.message uses events_default, not events['m.room.message']
        // This ensures compatibility with Element and other Matrix clients
        newPowerLevels.events_default = newLevel;
        break;
      case PERMISSION_EVENTS.CONFIGURE:
        newPowerLevels.state_default = newLevel;
        // Clear specific state event overrides when changing state_default.
        // This ensures "Configure room" permission actually controls all state events
        // (name, topic, avatar, etc.) without interference from stale overrides.
        // Per Matrix spec, specific event overrides in the events{} object take precedence
        // over state_default, so we must remove them for state_default to take effect.
        if (newPowerLevels.events) {
          const stateEventsToReset = [
            'm.room.name',
            'm.room.topic',
            'm.room.avatar',
            'm.room.join_rules',
            'm.room.history_visibility',
            GEMATIK_ROOM.ROOM_NAME,
            GEMATIK_ROOM.ROOM_TOPIC,
          ];
          for (const eventType of stateEventsToReset) {
            if (newPowerLevels.events[eventType] !== undefined) {
              delete newPowerLevels.events[eventType];
            }
          }
          // Clean up empty events object to keep power levels tidy
          if (Object.keys(newPowerLevels.events).length === 0) {
            delete newPowerLevels.events;
          }
        }
        break;
      case PERMISSION_EVENTS.BAN:
        newPowerLevels.ban = newLevel;
        break;
      case PERMISSION_EVENTS.KICK:
        newPowerLevels.kick = newLevel;
        break;
      case PERMISSION_EVENTS.REDACT:
        newPowerLevels.redact = newLevel;
        break;
      case PERMISSION_EVENTS.INVITE:
        newPowerLevels.invite = newLevel;
        break;
      case PERMISSION_EVENTS.HISTORICAL:
        if (!newPowerLevels.events) newPowerLevels.events = {};
        newPowerLevels.events['m.room.history_visibility'] = newLevel;
        break;
      default:
        throw new Error(`Unknown permission key: ${permissionKey}`);
      }

      // Validate power levels structure before sending
      validatePowerLevels(newPowerLevels);

      // Send updated power levels
      await client.sendStateEvent(roomId, 'm.room.power_levels', newPowerLevels, '');
      setPowerLevels(newPowerLevels);
      
      // Update local state
      setPermissionLevels(prev => ({
        ...prev,
        [permissionKey]: newLevel,
      }));

      logger.info('Updated permission level:', { permissionKey, newLevel });
    } catch (error) {
      logger.error('Error updating permission level:', error);
      throw error;
    }
  }, [client, roomId, canConfigure, powerLevels]);

  // Update room name (dual-write: Gematik + standard)
  const updateRoomName = useCallback(async (newName) => {
    if (!client || !roomId || !canEditName) {
      throw new Error('Cannot update room name: insufficient permissions or missing data');
    }
    try {
      const wrote = await dualWriteRoomName(client, roomId, newName, roomName);
      if (wrote) {
        setRoomName(newName.trim() || getRoomDisplayName(room));
      }
    } catch (error) {
      logger.error('Error updating room name:', error);
      throw error;
    }
  }, [client, roomId, canEditName, room, roomName]);

  // Update room topic (dual-write: Gematik + standard)
  const updateRoomTopic = useCallback(async (newTopic) => {
    if (!client || !roomId || !canEditTopic) {
      throw new Error('Cannot update room topic: insufficient permissions or missing data');
    }
    try {
      const wrote = await dualWriteRoomTopic(client, roomId, newTopic, roomTopic);
      if (wrote) {
        setRoomTopic(newTopic.trim());
      }
    } catch (error) {
      logger.error('Error updating room topic:', error);
      throw error;
    }
  }, [client, roomId, canEditTopic, roomTopic]);

  return {
    // State
    room,
    roomName,
    isLoading,
    canConfigure,
    userPowerLevel,
    isDM,
    isEncrypted,
    
    // Security & Privacy
    joinRule,
    historyVisibility,
    
    // Permissions
    permissionLevels,
    powerLevels,
    
    // Room Details
    roomTopic,
    roomVersion,
    canEditName,
    canEditTopic,

    // Actions
    updateJoinRule,
    updateHistoryVisibility,
    updatePermissionLevel,
    updateRoomName,
    updateRoomTopic,
  };
};

export default useRoomSettings;
